import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../../../util_components/button';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import RequiredReasonTextarea from '../../../util_components/required_reason_textarea';
import { FormActions } from '../../../util_components/form_layout';
import {
  CareRequestFields,
  CareSaveRetry,
  TeacherNoteField
} from '../../../util_components/care_request';
import {
  INTERNAL_NOTE_LABEL,
  INTERNAL_NOTE_PLACEHOLDER,
  KIND_TEACHER_CHANGE,
  build_create_payload,
  care_api_error,
  covers_care
} from '../../../util_components/care_request/care_request_rules';
import { post_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { api_error_message } from '../../../../utils/api_error_message';
import useCareCapture from './use_care_capture';
import RequestTimeWarningStep from '../../../util_components/request_time_warning';
import useRequestTimeWarning from '../../../util_components/request_time_warning/use_request_time_warning';
import {
  client_request_time_warning,
  with_request_time_ack
} from '../../../util_components/request_time_warning/request_time_warning_rules';
import PayTimeWarningStep from '../../../util_components/pay_time_warning';
import usePayTimeWarning from '../../../util_components/pay_time_warning/use_pay_time_warning';
import { with_pay_time_ack } from '../../../util_components/pay_time_warning/pay_time_warning_rules';
import {
  plan_teacher_change,
  teacher_change_payload,
  teacher_options_of
} from './session_change_rules';

// The session page's "Change Teacher" body; mounted per open. Care's details apply on every session type it covers.
function ChangeTeacherForm(props) {
  const { session_uuid, change_session_uuid, session_type, teachers, class_start, booked_at } =
    props;
  const rt = useRequestTimeWarning();
  const [teacher_uuid, setTeacher] = useState('');
  const [reason, setReason] = useState('');
  const [reason_error, setReasonError] = useState(false);
  const [busy, setBusy] = useState(false);
  const pw = usePayTimeWarning();
  // One swap per click: the ref closes the window before the state lands (upload + swap take seconds).
  const in_flight = useRef(false);
  const rt_ack = useRef(null);
  const care = useCareCapture({
    session_uuid,
    kind: covers_care(KIND_TEACHER_CHANGE, session_type) ? KIND_TEACHER_CHANGE : null,
    on_saved: props.on_care_saved,
    on_retry_change: props.on_retry_change
  });

  // pay_ack: the pay-time acknowledgement (apis warned on the swap); the request-time one (Care
  // record) is kept in rt_ack between the two warning steps
  const submit = async (pay_ack = null) => {
    const plan = plan_teacher_change({
      change_reason: reason,
      care_draft: care.draft,
      teacher_uuid,
      now_ms: Date.now()
    });
    if (plan.reason_error) {
      setReasonError(true);
      return;
    }
    if (plan.care_errors) {
      care.setErrors(plan.care_errors);
      return;
    }
    if (plan.teacher_missing || in_flight.current) return;
    const pre =
      !rt_ack.current && care.draft
        ? client_request_time_warning({
            requested_local: care.draft.requested_at,
            class_start,
            booked_at
          })
        : null;
    if (pre) {
      rt.show(pre);
      return;
    }
    in_flight.current = true;
    setBusy(true);
    const settle = () => {
      in_flight.current = false;
      setBusy(false);
    };
    let screenshot_key = '';
    try {
      screenshot_key = await care.upload_once();
    } catch (e) {
      settle();
      props.notify(
        'error',
        care_api_error(e, 'The screenshot could not be uploaded. Nothing was changed.').message
      );
      return;
    }
    // Submit stays disabled until the Care save settles: success via done, failure via the Retry dialog.
    const done = () => {
      settle();
      props.on_done();
    };
    const payload = with_pay_time_ack(
      teacher_change_payload({
        session_uuid: change_session_uuid,
        teacher_uuid,
        reason: plan.reason,
        care_draft: care.draft
      }),
      pay_ack
    );
    post_api(get_api_url('change_teacher_for_session'), payload, true)
      .then(() => {
        pw.clear();
        if (!care.draft) {
          done();
          return;
        }
        care.save_after_change(
          with_request_time_ack(
            build_create_payload(care.draft, {
              session_uuid: change_session_uuid,
              note: plan.reason,
              screenshot_key,
              expected_teacher_uuid: teacher_uuid
            }),
            rt_ack.current
          ),
          done
        );
      })
      .catch((e) => {
        settle();
        if (pw.catch_warning(e)) return;
        pw.clear();
        props.notify('error', api_error_message(e));
      });
  };

  if (rt.warning && !care.retry) {
    return (
      <RequestTimeWarningStep
        warning={rt.warning}
        requestedLocal={care.draft ? care.draft.requested_at : ''}
        busy={busy}
        onAcknowledge={(ack) => {
          rt_ack.current = ack;
          rt.clear();
          submit();
        }}
        onChangeTime={rt.clear}
      />
    );
  }
  if (pw.warning) {
    return (
      <>
        <h5>Change Teacher</h5>
        <PayTimeWarningStep
          warning={pw.warning}
          busy={busy}
          onAcknowledge={(ack) => submit(ack)}
          onChange={() => {
            // back to the form: a changed time is checked again
            rt_ack.current = null;
            pw.clear();
          }}
        />
      </>
    );
  }

  return (
    <>
      <h5>Change Teacher</h5>
      {props.warning ? (
        <p
          style={{
            margin: '10px 0',
            padding: '8px',
            maxWidth: '400px',
            color: 'var(--state-warning-text)',
            background: 'var(--state-warning-bg)'
          }}
        >
          {props.warning}
        </p>
      ) : null}
      {props.gender_caption ? <p className="myt-form-help">{props.gender_caption}</p> : null}
      <CustomSingleSelect
        label="Select a teacher"
        options={teacher_options_of(teachers)}
        onChange={(e) => setTeacher(e.target.value)}
        value={teacher_uuid || ''}
        data-session_id={change_session_uuid}
      />
      <RequiredReasonTextarea
        value={reason || ''}
        onChange={(e) => {
          setReason(e.target.value);
          setReasonError(false);
        }}
        label={INTERNAL_NOTE_LABEL}
        placeholder={INTERNAL_NOTE_PLACEHOLDER}
        error={!!reason_error}
        errorText="An internal note (at least 3 characters) is required to change the teacher"
        height="60px"
      />
      {care.draft ? (
        <TeacherNoteField
          value={care.draft.teacher_note || ''}
          onChange={(teacher_note) => care.patch({ teacher_note })}
          error={care.errors.teacher_note}
        />
      ) : null}
      {care.draft ? (
        <CareRequestFields
          draft={care.draft}
          errors={care.errors}
          onChange={care.patch}
          initiatedByFixed="TEACHER"
          requestedLabel="Teacher request time (IST)"
          bookedAt={booked_at}
        />
      ) : null}
      {care.retry ? (
        <CareSaveRetry
          message={care.retry.message}
          busy={care.retry.busy}
          warning={care.retry.warning}
          requestedAt={care.retry.payload.requested_at}
          onRetry={care.retry_save}
          onClose={props.on_close}
        />
      ) : (
        <FormActions>
          <Button data-session_id={change_session_uuid} disabled={!!busy} onClick={() => submit()}>
            {busy ? 'Submitting…' : 'Submit'}
          </Button>
        </FormActions>
      )}
    </>
  );
}

ChangeTeacherForm.propTypes = {
  session_uuid: PropTypes.string,
  change_session_uuid: PropTypes.string,
  session_type: PropTypes.string,
  teachers: PropTypes.arrayOf(PropTypes.object).isRequired,
  warning: PropTypes.string,
  gender_caption: PropTypes.string,
  on_done: PropTypes.func.isRequired,
  on_close: PropTypes.func.isRequired,
  on_care_saved: PropTypes.func.isRequired,
  on_retry_change: PropTypes.func.isRequired,
  notify: PropTypes.func.isRequired,
  class_start: PropTypes.number,
  booked_at: PropTypes.string
};

ChangeTeacherForm.defaultProps = {
  session_uuid: '',
  change_session_uuid: '',
  session_type: '',
  warning: '',
  gender_caption: '',
  class_start: undefined,
  booked_at: ''
};

export default ChangeTeacherForm;
